import { useState, useEffect, useCallback } from 'react';
import type { AppDetailsData } from '@/api-types';
import { browserDB } from '@/services/browser-storage/indexedDB';
import { useAuthStore } from '@/stores/authStore';

export function useApp(appId: string | undefined) {
  // Always declare hooks in the same order to satisfy React Rules of Hooks
  const [app, setApp] = useState<AppDetailsData | null>(null);
  const [loading, setLoading] = useState<boolean>(!!appId && appId !== 'new');
  const [error, setError] = useState<string | null>(null);

  const fetchApp = useCallback(async () => {
    // Guard: if no valid appId or it's a new app, reset state and skip fetching
    if (!appId || appId === 'new') {
      setApp(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // For CSR architecture, fetch app from browser storage
      const project = await browserDB.getProject(appId);
      if (!project) {
        throw new Error('App not found');
      }

      // Get the latest code generation for this project
      const codeGeneration = project.id ? await browserDB.getLatestCodeGeneration(project.id) : undefined;
      const rawFiles = codeGeneration ? JSON.parse(codeGeneration.files) : [] as any[];
      const normalizedFiles: Array<{ path: string; content: string; language?: string }> = Array.isArray(rawFiles)
        ? rawFiles.map((f: any) => {
            if (f && typeof f === 'object') {
              // Support both legacy { path, content, language } and current { filePath, fileContents, language }
              const path = typeof f.path === 'string' ? f.path : (typeof f.filePath === 'string' ? f.filePath : '');
              const content = typeof f.content === 'string' ? f.content : (typeof f.fileContents === 'string' ? f.fileContents : '');
              const language = typeof f.language === 'string' ? f.language : undefined;
              if (path) return { path, content, language };
            }
            return { path: '', content: '' };
          })
          .filter((f: { path: string; content: string }) => f.path && typeof f.content === 'string')
        : [];

      // Check if user has starred this app (simplified implementation)
      const authStore = useAuthStore.getState();
      const user = authStore.user;
      const isStarred = user ? await browserDB.isAppStarred(user.id, appId) : false;

      const flattenedUser = { id: project.userId, name: 'User', avatar: undefined };
      const appIdStr = String(project.id ?? project.agentId);
      const appData: AppDetailsData = {
        app: {
          id: appIdStr,
          title: project.title,
          description: project.description || '',
          userId: project.userId,
          isPublic: false,
          visibility: 'private',
          createdAt: project.createdAt,
          updatedAt: project.updatedAt,
          user: flattenedUser,
          starCount: 0,
          viewCount: 0
        },
        files: normalizedFiles.map((file) => ({
          path: file.path,
          content: file.content,
          language: file.language
        })),
        isStarred,
        // Flattened fields for legacy components
        id: appIdStr,
        title: project.title,
        description: project.description || '',
        userId: project.userId,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
        visibility: 'private',
        user: flattenedUser,
        starCount: 0,
        viewCount: 0,
        agentSummary: {
          generatedCode: normalizedFiles.map((file) => ({
            filePath: file.path,
            fileContents: file.content,
          }))
        }
      };

      setApp(appData);
      setError(null);
    } catch (err) {
      console.error('Error fetching app:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch app');
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    void fetchApp();
  }, [fetchApp]);

  return { app, loading, error, refetch: fetchApp };
}
